import {
  BitkitActionMenu,
  BitkitButton,
  BitkitControlButton,
  BitkitIconComponent,
  BitkitList,
  createBitkitToast,
  IconBranch,
  IconCalendar,
  IconClock,
  IconErrorCircleFilled,
  IconMoreVertical,
  IconPause,
  IconPencil,
  IconPlay,
  IconTrash,
  IconWorkflow,
  IconWorkflowFlow,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { Card, type CardRootProps } from '@chakra-ui/react/card';
import { useDisclosure } from '@chakra-ui/react/hooks';
import { Text } from '@chakra-ui/react/text';
import { parseISO } from 'date-fns';
import { ReactNode, useContext, useMemo } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import useInitialState from '@/hooks/useInitialState';
import useStartBuild from '@/hooks/useStartBuild';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import useUpdateScheduledBuild from '@/pages/App/AppDetails/components/ScheduledBuilds/hooks/useUpdateScheduledBuild';
import { numericToWeekdayIndexes } from '@/pages/App/components/StartBuildDialog/utils/daysToNumeric';
import AppForAppPage from '@/pages/App/types/AppForAppPage';

import { triggerNowPath } from '../../../../../utils/monolithApiRouteService';
import { queryClient } from '../../../../../utils/query';
import BuildScheduleDateTime from '../BuildScheduleTime/BuildScheduleDateTime';
import DeleteScheduledBuildDialog from './DeleteScheduledBuildDialog';
import { ScheduledBuild } from './scheduledBuilds.types';
import parseBuildParams from './utils/parseBuildParams';

interface MetaProps extends BoxProps {
  icon: BitkitIconComponent;
  text?: string;
}

const Meta = ({ children, icon: Icon, text, ...rest }: MetaProps) => (
  <Box display="flex" alignItems="center" gap="4" paddingInline="12" paddingBlock="8" color="text/secondary" {...rest}>
    <Icon size="16" />
    {!!text && (
      <Text as="span" textStyle="body/md/regular">
        {text}
      </Text>
    )}
    {children}
  </Box>
);

export interface ScheduledBuildItemProps extends CardRootProps {
  app: AppForAppPage;
  scheduledBuildItemData: ScheduledBuild;
  onUpdateScheduledBuild: (scheduledBuild: ScheduledBuild) => void;
  lastErrorMessage?: string;
}

const ScheduledBuildItem = ({
  app,
  scheduledBuildItemData,
  onUpdateScheduledBuild,
  lastErrorMessage,
  ...rest
}: ScheduledBuildItemProps) => {
  const { user } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const { isMobile } = useResponsive();

  const {
    id,
    buildParams,
    isEnabled: initialIsEnabled,
    nextRun,
    repetitionEnabled,
    repeatEvery,
    repeatInterval,
    startDate,
    monthlyRepetition,
    yearlyRepetition,
    setupLocation,
    weekdays,
    cronExpression,
  } = scheduledBuildItemData;

  const parsedBuildParams = useMemo(() => {
    const parsed = parseBuildParams(buildParams);
    if (!parsed.hookInfo) {
      parsed.hookInfo = {
        buildTriggerToken: '',
        type: 'bitrise',
      };
    }
    parsed.hookInfo.buildTriggerToken = app.buildTriggerToken;
    if (parsed.hookInfo.apiToken) {
      delete parsed.hookInfo.apiToken;
    }
    return parsed;
  }, [buildParams, app.buildTriggerToken]);

  const [isEnabled, setIsEnabled] = useInitialState(initialIsEnabled);

  const { onClose: onDeleteDialogClose, onOpen: onDeleteDialogOpen, open: isDeleteDialogOpen } = useDisclosure();

  let cardStatusColor: string;
  if (lastErrorMessage) {
    cardStatusColor = 'border/error';
  } else if (isEnabled) {
    cardStatusColor = 'color/blue/base';
  } else {
    cardStatusColor = 'color/neutral/strong';
  }

  let StatusIcon: BitkitIconComponent;
  let statusIconColor: string;
  if (lastErrorMessage) {
    StatusIcon = IconErrorCircleFilled;
    statusIconColor = 'icon/negative';
  } else if (isEnabled) {
    StatusIcon = IconCalendar;
    statusIconColor = 'status/info/icon';
  } else {
    StatusIcon = IconPause;
    statusIconColor = 'icon/secondary';
  }

  const style = {
    card: {
      position: 'relative',
      paddingInlineStart: '7px',
      _before: {
        content: `""`,
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '7px',
        backgroundColor: cardStatusColor,
        borderTopLeftRadius: '8',
        borderBottomLeftRadius: '8',
      },
    },
    up: {
      display: 'flex',
      alignItems: 'center',
      flexGrow: '1',
      gap: '12',
      padding: '12',
      maxWidth: '100%',
      overflow: 'hidden',
    },
  };

  const { mutate: startBuild } = useStartBuild(
    {
      appSlug: app.slug,
      buildTriggerToken: app.buildTriggerToken,
      path: triggerNowPath(id),
    },
    {
      onSuccess: ({ data }) => {
        if (data.results?.length === 1 && data.results[0].buildUrl) {
          window.location.href = data.results[0].buildUrl;
        }
      },
      onError: (error) => {
        let errorTitle = 'Failed to start build';
        let errorDescription: ReactNode = 'Unknown error';
        if (error.response?.data.errorMsg) {
          errorDescription = error.response?.data.errorMsg;
        } else if (error.response?.data.results?.length) {
          const resultErrors: string[] = [];
          error.response?.data.results.forEach((result) => {
            if (result.status === 'error') {
              resultErrors.push(`${result.triggeredPipeline || result.triggeredWorkflow}: ${result.message}`);
            }
          });
          if (resultErrors.length > 1) {
            errorTitle += 's';
          }
          errorDescription = (
            <BitkitList>
              {resultErrors.map((err) => (
                <BitkitList.Item key={err}>{err}</BitkitList.Item>
              ))}
            </BitkitList>
          );
        } else {
          errorDescription = error.response?.data.message || 'Unknown error';
        }
        createBitkitToast({
          titleText: errorTitle,
          messageText: errorDescription,
          variant: 'critical',
          dismissible: true,
        });
        queryClient.refetchQueries({ queryKey: ['scheduledBuilds', app.slug] });
      },
      requestConfig: {
        params: { build_trigger_token: app.buildTriggerToken },
      },
    },
  );

  const { mutate: updateScheduledBuild } = useUpdateScheduledBuild({
    app,
  });

  const updateScheduledBuildIsEnable = (updatedValue: boolean) => {
    setIsEnabled(updatedValue);

    const updatedSchedule: any = {
      ...scheduledBuildItemData,
      buildParams: parsedBuildParams,
      isEnabled: updatedValue,
    };

    if (
      Number.isInteger(scheduledBuildItemData.hourAtSetupLocation) &&
      Number.isInteger(scheduledBuildItemData.minuteAtSetupLocation)
    ) {
      updatedSchedule.time = `${scheduledBuildItemData.hourAtSetupLocation.toString().padStart(2, '0')}:${scheduledBuildItemData.minuteAtSetupLocation.toString().padStart(2, '0')}`;
    }

    updateScheduledBuild({
      schedule: updatedSchedule,
    });
  };

  return (
    <Card.Root role="group" css={style.card} {...rest}>
      <Box
        display="flex"
        flexDirection={['column', 'row']}
        alignItems={['flex-start', 'center']}
        borderBottom="1px solid"
        borderBottomColor="border/minimal"
      >
        <Box css={style.up}>
          <StatusIcon size="24" color={statusIconColor} />
          {isMobile ? (
            <BuildScheduleDateTime
              nextRun={parseISO(nextRun)}
              repetitionEnabled={repetitionEnabled}
              repeatEvery={repeatEvery}
              repeatInterval={repeatInterval}
              startDate={startDate}
              monthlyRepetition={monthlyRepetition}
              yearlyRepetition={yearlyRepetition}
              setupLocation={setupLocation}
              weekdayIndexes={numericToWeekdayIndexes(weekdays)}
              cronExpression={cronExpression}
              textStyle="body/md/regular"
              whiteSpace="normal"
            />
          ) : (
            <Box display="flex" flexDir="column" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
              <Text as="span" truncate textStyle="body/md/regular">
                {parsedBuildParams.buildParams.commitMessage || 'No commit message.'}
              </Text>
              {lastErrorMessage && (
                <Text truncate textStyle="body/md/regular" color="status/critical/text">
                  {lastErrorMessage}
                </Text>
              )}
            </Box>
          )}
        </Box>
        {isMobile && (
          <Box css={style.up} borderTop="1px solid" borderTopColor="border/minimal">
            <Text as="span" truncate maxWidth="720px" textStyle="body/md/regular">
              {parsedBuildParams.buildParams.commitMessage || 'No commit message.'}
            </Text>
          </Box>
        )}
        {!isMobile && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            gap="16"
            marginRight={12}
            flexShrink="0"
          >
            <BitkitButton
              variant="tertiary"
              size="sm"
              icon={IconPlay}
              whiteSpace="nowrap"
              onClick={() =>
                startBuild({ buildParams: parsedBuildParams.buildParams, triggeredBy: `manual-${user.username}` })
              }
            >
              Trigger now
            </BitkitButton>
            <BitkitActionMenu trigger={<BitkitControlButton icon={IconMoreVertical} label="More actions" size="sm" />}>
              <BitkitActionMenu.Item icon={IconPencil} onClick={() => onUpdateScheduledBuild(scheduledBuildItemData)}>
                Edit configuration
              </BitkitActionMenu.Item>
              <BitkitActionMenu.Item
                icon={isEnabled ? IconPause : IconPlay}
                onClick={() => updateScheduledBuildIsEnable(!isEnabled)}
              >
                {isEnabled ? 'Pause' : 'Start'} schedule
              </BitkitActionMenu.Item>
              <BitkitActionMenu.Item icon={IconTrash} isDanger onClick={onDeleteDialogOpen}>
                Delete
              </BitkitActionMenu.Item>
            </BitkitActionMenu>
            <DeleteScheduledBuildDialog
              isOpen={isDeleteDialogOpen}
              onClose={onDeleteDialogClose}
              scheduledBuildItemId={scheduledBuildItemData.id}
              app={app}
            />
          </Box>
        )}
      </Box>
      <Box display="flex" justifyContent={['center', 'flex-end']} flexDirection={['column', 'row']}>
        <Meta icon={IconBranch} text={parsedBuildParams.buildParams.branch} maxWidth="100%" />
        {(parsedBuildParams.buildParams.workflowId || parsedBuildParams.buildParams.pipelineId) && (
          <Meta
            icon={parsedBuildParams.buildParams.workflowId ? IconWorkflow : IconWorkflowFlow}
            text={parsedBuildParams.buildParams.workflowId || parsedBuildParams.buildParams.pipelineId}
            maxWidth="100%"
            borderTop={['1px solid', 'none']}
            borderTopColor="border/minimal"
          />
        )}
        {!isMobile && (
          <Meta icon={IconClock} marginInlineStart="auto" maxWidth="100%">
            <BuildScheduleDateTime
              nextRun={parseISO(nextRun)}
              repetitionEnabled={repetitionEnabled}
              repeatEvery={repeatEvery}
              repeatInterval={repeatInterval}
              startDate={startDate}
              monthlyRepetition={monthlyRepetition}
              yearlyRepetition={yearlyRepetition}
              setupLocation={setupLocation}
              weekdayIndexes={numericToWeekdayIndexes(weekdays)}
              cronExpression={cronExpression}
              textStyle="body/md/regular"
            />
          </Meta>
        )}
      </Box>
      {isMobile && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          gap="16"
          paddingRight={12}
          width="100%"
          borderTop="1px solid"
          borderTopColor="border/minimal"
        >
          <BitkitButton
            variant="tertiary"
            size="lg"
            icon={IconPlay}
            onClick={() =>
              startBuild({ buildParams: parsedBuildParams.buildParams, triggeredBy: `manual-${user.username}` })
            }
          >
            Trigger now
          </BitkitButton>
          <BitkitActionMenu trigger={<BitkitControlButton icon={IconMoreVertical} label="More actions" size="sm" />}>
            <BitkitActionMenu.Item icon={IconPencil} onClick={() => onUpdateScheduledBuild(scheduledBuildItemData)}>
              Edit configuration
            </BitkitActionMenu.Item>
            <BitkitActionMenu.Item
              icon={isEnabled ? IconPause : IconPlay}
              onClick={() => updateScheduledBuildIsEnable(!isEnabled)}
            >
              {isEnabled ? 'Pause' : 'Start'} schedule
            </BitkitActionMenu.Item>
            <BitkitActionMenu.Item icon={IconTrash} isDanger onClick={onDeleteDialogOpen}>
              Delete
            </BitkitActionMenu.Item>
          </BitkitActionMenu>
          <DeleteScheduledBuildDialog
            isOpen={isDeleteDialogOpen}
            onClose={onDeleteDialogClose}
            scheduledBuildItemId={scheduledBuildItemData.id}
            app={app}
          />
        </Box>
      )}
    </Card.Root>
  );
};

export default ScheduledBuildItem;
